import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays } from 'date-fns';
import { useRestaurantContext } from '../../context/RestaurantContext';
import {
  getReservationAvailability,
  createCustomerReservation,
} from '../../services/api/restApiAdapter';
import TopBarHeader from '../../components/ui/TopBarHeader';
import Icon from '../../components/AppIcon';

export default function ReservationFlow() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const { theme, restaurantData } = useRestaurantContext();

  const themeColor = theme?.secondary || '#0f172a';
  const themeTextColor = theme?.primary || '#ffffff';

  // Step state: 1 = Selection (Party, Date, Time), 2 = Guest Info, 3 = Confirmation
  const [step, setStep] = useState(1);

  // Booking Form State
  const [partySize, setPartySize] = useState(2);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedTime, setSelectedTime] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');

  // Availability State
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch slots whenever date or party size changes
  useEffect(() => {
    let isCancelled = false;

    const fetchSlots = async () => {
      setIsLoadingSlots(true);
      setErrorMessage('');
      try {
        const res = await getReservationAvailability(restaurantId, {
          date: selectedDate,
          partySize,
        });

        if (!isCancelled) {
          if (res?.availableSlots) {
            setAvailableSlots(res.availableSlots);
            // Pre-select first available slot if current is invalid
            if (res.availableSlots.length > 0 && !res.availableSlots.includes(selectedTime)) {
              setSelectedTime(res.availableSlots[0]);
            } else if (res.availableSlots.length === 0) {
              setSelectedTime('');
            }
          } else {
            setAvailableSlots([]);
            setSelectedTime('');
          }
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to fetch reservation slots:', err);
          setAvailableSlots([]);
        }
      } finally {
        if (!isCancelled) setIsLoadingSlots(false);
      }
    };

    if (restaurantId && selectedDate) {
      fetchSlots();
    }

    return () => {
      isCancelled = true;
    };
  }, [restaurantId, selectedDate, partySize]);

  // Handle Form Submission
  const handleConfirmBooking = async (e) => {
    e.preventDefault();
    if (!customerName || !customerPhone || !selectedTime) return;

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const res = await createCustomerReservation(restaurantId, {
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        partySize,
        reservationDate: selectedDate,
        startTime: selectedTime,
        specialRequests: specialRequests.trim() || undefined,
      });

      if (res) {
        setBookingResult(res);
        setStep(3); // Go to confirmation step
      } else {
        setErrorMessage('Failed to confirm reservation. Please choose another slot.');
      }
    } catch (err) {
      console.error('Booking submission error:', err);
      setErrorMessage(err.message || 'Could not complete reservation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Next 7 days generator
  const dateOptions = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(new Date(), i);
    return {
      dateStr: format(d, 'yyyy-MM-dd'),
      dayName: i === 0 ? 'Today' : i === 1 ? 'Tmrw' : format(d, 'EEE'),
      dayNumber: format(d, 'd'),
      month: format(d, 'MMM'),
    };
  });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Bar */}
      <TopBarHeader
        title="Reserve a Table"
        onBack={() => {
          if (step > 1 && step < 3) {
            setStep(step - 1);
          } else {
            navigate(`/restaurant/${restaurantId}`);
          }
        }}
      />

      <div className="flex-1 max-w-lg mx-auto w-full p-4 pb-24 space-y-6">
        {errorMessage && (
          <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl text-xs flex items-center gap-2">
            <Icon name="AlertCircle" size={16} />
            <span>{errorMessage}</span>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* STEP 1: Date, Party & Time Slot Selection */}
          {step === 1 && (
            <motion.div
              key="step-1"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              {/* Party Size Selector */}
              <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Number of Guests
                  </span>
                  <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
                    {partySize} {partySize === 1 ? 'Guest' : 'Guests'}
                  </span>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setPartySize(num)}
                      className={`flex-shrink-0 w-12 h-12 rounded-2xl font-bold text-sm transition-all flex items-center justify-center ${
                        partySize === num
                          ? 'bg-slate-900 text-white shadow-md scale-105'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Selector */}
              <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 space-y-3">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Select Date
                </span>
                <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-none">
                  {dateOptions.map((opt) => {
                    const isSelected = selectedDate === opt.dateStr;
                    return (
                      <button
                        key={opt.dateStr}
                        type="button"
                        onClick={() => setSelectedDate(opt.dateStr)}
                        className={`flex-shrink-0 w-16 py-3 rounded-2xl flex flex-col items-center gap-1 transition-all ${
                          isSelected
                            ? 'bg-indigo-600 text-white shadow-md scale-105'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        <span className="text-[11px] font-semibold opacity-80">{opt.dayName}</span>
                        <span className="text-lg font-black">{opt.dayNumber}</span>
                        <span className="text-[10px] font-medium opacity-70">{opt.month}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time Slots */}
              <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Available Time Slots
                  </span>
                  {selectedTime && (
                    <span className="text-xs font-bold text-emerald-600">Selected: {selectedTime}</span>
                  )}
                </div>

                {isLoadingSlots ? (
                  <div className="py-8 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" />
                    <span>Checking table availability...</span>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="py-8 text-center text-slate-500 text-xs bg-slate-50 rounded-2xl p-4">
                    No table slots available for {partySize} guests on this date. Please try another day or party size.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2.5 max-h-56 overflow-y-auto pr-1">
                    {availableSlots.map((slot) => {
                      const isSelected = selectedTime === slot;
                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setSelectedTime(slot)}
                          className={`py-3 rounded-xl font-bold text-xs transition-all border ${
                            isSelected
                              ? 'bg-slate-900 border-slate-900 text-white shadow-md'
                              : 'bg-white border-slate-200 text-slate-800 hover:border-slate-300'
                          }`}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Bottom Continue Button */}
              <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur border-t border-slate-200/80 z-20">
                <div className="max-w-lg mx-auto">
                  <button
                    type="button"
                    disabled={!selectedTime}
                    onClick={() => setStep(2)}
                    className="w-full py-3.5 px-6 rounded-2xl bg-indigo-600 text-white font-bold text-sm shadow-lg shadow-indigo-600/25 transition-all hover:bg-indigo-700 disabled:opacity-50 disabled:shadow-none"
                  >
                    Continue to Guest Details
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Guest Information */}
          {step === 2 && (
            <motion.div
              key="step-2"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              {/* Summary Pill */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex items-center justify-between text-xs text-indigo-900">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={16} className="text-indigo-600" />
                  <span className="font-semibold">{format(new Date(selectedDate), 'EEE, MMM d')} at {selectedTime}</span>
                </div>
                <span className="font-bold bg-white px-2 py-0.5 rounded-md border border-indigo-200">
                  {partySize} Guests
                </span>
              </div>

              {/* Guest Form */}
              <form onSubmit={handleConfirmBooking} className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-4">
                <h3 className="text-sm font-bold text-slate-800">Your Contact Details</h3>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="e.g. Alex Johnson"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="e.g. 9876543210"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">We will send table confirmation to this number.</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Special Requests (Optional)</label>
                  <textarea
                    rows="2"
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    placeholder="e.g. Quiet corner, high chair, birthday surprise"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting || !customerName || !customerPhone}
                    className="w-full py-3.5 px-6 rounded-2xl bg-indigo-600 text-white font-bold text-sm shadow-lg shadow-indigo-600/25 transition-all hover:bg-indigo-700 disabled:opacity-50 disabled:shadow-none"
                  >
                    {isSubmitting ? 'Reserving Table...' : 'Confirm Reservation'}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* STEP 3: Booking Confirmation Screen */}
          {step === 3 && bookingResult && (
            <motion.div
              key="step-3"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-6 shadow-xl border border-slate-100 text-center space-y-6"
            >
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                <Icon name="CheckCircle" size={36} />
              </div>

              <div>
                <h2 className="text-xl font-black text-slate-900">Table Reserved!</h2>
                <p className="text-xs text-slate-500 mt-1">We look forward to welcoming you.</p>
              </div>

              {/* Receipt / Details Card */}
              <div className="bg-slate-50 rounded-2xl p-4 text-left border border-slate-100 space-y-2.5 text-xs text-slate-700">
                <div className="flex justify-between border-b border-slate-200/60 pb-2">
                  <span className="text-slate-400">Booking Name</span>
                  <span className="font-bold text-slate-900">{bookingResult.customerName}</span>
                </div>
                <div className="flex justify-between border-b border-slate-200/60 pb-2">
                  <span className="text-slate-400">Date & Time</span>
                  <span className="font-bold text-slate-900">
                    {format(new Date(bookingResult.reservationDate), 'EEE, MMM d, yyyy')} at {bookingResult.startTime}
                  </span>
                </div>
                <div className="flex justify-between border-b border-slate-200/60 pb-2">
                  <span className="text-slate-400">Party Size</span>
                  <span className="font-bold text-slate-900">{bookingResult.partySize} Guests</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Table</span>
                  <span className="font-bold text-indigo-600">
                    {bookingResult.tableName || (bookingResult.tableNumber ? `Table ${bookingResult.tableNumber}` : 'Allocated on arrival')}
                  </span>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate(`/restaurant/${restaurantId}`)}
                  className="w-full py-3.5 rounded-2xl bg-slate-900 text-white font-bold text-sm shadow-md hover:bg-slate-800 transition-all"
                >
                  Explore Menu / Back to Home
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
